import { useState, useEffect, useCallback } from 'react';
import { useForm, type SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Box, Stack, Typography, Button } from '@mui/material';
import useLocalization from '../../../hooks/useLocalization';
import { FormTextField } from '../../molecules/form-elements';
import PhoneNumberInput from '../../atoms/PhoneNumberInput';
import StyledForm from '../shared/StyledForm';
import { useLigdiCash } from '../../../hooks/payment/useLigdiCash';
import { EnumValues, PAYMENT_METHOD } from '../../../types/enums';
import { TFunction } from 'i18next';
import Hint from '../../atoms/Hint';
import isEmpty from 'lodash-es/isEmpty';
import { createPhoneSchema, normalizePhoneNumber } from '../../../schemas/helpers/phoneValidation';

const styles = {
  stepContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  otpContainer: {
    textAlign: 'center',
    mb: 2,
  },
};

const FORM_STEPS = {
  PAYMENT_DETAILS: 'PAYMENT_DETAILS',
  OTP_VERIFICATION: 'OTP_VERIFICATION',
} as const;

type FormStep = EnumValues<typeof FORM_STEPS>;

const paymentDetailsSchema = (t: TFunction) => {
  return z.object({
    amount: z.coerce.number({ error: t('invalidNumber') }).int({ error: t('noDecimalNumbersAllowed') }),
    phone_number: createPhoneSchema(t, true),
  });
};

const formSchema = (t: TFunction) => {
  return paymentDetailsSchema(t).extend({
    otp: z.string({ error: t('otpRequired') }).length(6, t('otpMustBe6Digits')),
  });
};

type FormInput = z.input<ReturnType<typeof formSchema>>;
type PaymentDetailsData = z.output<ReturnType<typeof paymentDetailsSchema>>;
type FormData = z.output<ReturnType<typeof formSchema>>;
type DepositFormData = PaymentDetailsData & { otp?: string };

const LigdiCashDepositForm = ({ paymentProvider }: { paymentProvider: string | null }) => {
  const { t } = useLocalization();
  const [currentStep, setCurrentStep] = useState<FormStep>(FORM_STEPS.PAYMENT_DETAILS);
  const schema = formSchema(t);
  const detailsSchema = paymentDetailsSchema(t);

  const {
    initiateDepositMutation: { mutate: initiateDeposit, isPending: isInitiateDepositPending },
    depositMutation: { mutate: deposit, isPending: isDepositPending },
    providerConfig,
  } = useLigdiCash(paymentProvider);

  const { control, handleSubmit, trigger, getValues, reset, formState } = useForm<FormInput, unknown, FormData>({
    resolver: zodResolver(schema),
    defaultValues: { amount: undefined, phone_number: '+226', otp: undefined },
  });

  useEffect(() => {
    reset();
    setCurrentStep(FORM_STEPS.PAYMENT_DETAILS);
  }, [paymentProvider, reset]);

  const finalizeDeposit = useCallback<SubmitHandler<DepositFormData>>(
    (data) => {
      deposit({ ...data, phone_number: normalizePhoneNumber(data.phone_number), method: paymentProvider || '' });
    },
    [deposit, paymentProvider]
  );

  const handleFormSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    // To avoid having two separate useForm hooks, we validate the form fields based on the current step with 'trigger'
    if (currentStep === FORM_STEPS.PAYMENT_DETAILS) {
      const fieldsToValidate: (keyof FormInput)[] = ['amount', 'phone_number'];
      if (providerConfig.otpOnInitialStep) {
        fieldsToValidate.push('otp');
      }

      const isValid = await trigger(fieldsToValidate);
      if (!isValid) return;

      const formValues = getValues();
      const paymentDetails = detailsSchema.parse(formValues);

      if (providerConfig.requiresOtpStep) {
        initiateDeposit(
          { ...paymentDetails, phone_number: normalizePhoneNumber(paymentDetails.phone_number) },
          {
            onSuccess: (response) => {
              if (isEmpty(response.authResults)) {
                setCurrentStep(FORM_STEPS.OTP_VERIFICATION);
              }
            },
          }
        );
      } else {
        const data = providerConfig.otpOnInitialStep ? schema.parse(formValues) : paymentDetails;
        finalizeDeposit(data);
      }
    } else {
      handleSubmit(finalizeDeposit)();
    }
  };

  const isSubmitting = isInitiateDepositPending || isDepositPending;

  const otpInput = () => {
    return (
      <FormTextField
        name='otp'
        control={control}
        label={t('otpCode')}
        fullWidth
        error={!!formState.errors.otp}
        helperText={formState.errors.otp?.message}
        disabled={isSubmitting}
      />
    );
  };

  const renderPaymentStep = () => {
    const isOrangeMoney = paymentProvider === PAYMENT_METHOD.ORANGE_MONEY_BURKINA;

    return (
      <Box sx={styles.stepContainer}>
        {isOrangeMoney && <Hint text={t('orangeMoneyHint')} />}
        <FormTextField
          name='amount'
          control={control}
          label={t('amount')}
          fullWidth
          type='number'
          error={!!formState.errors.amount}
          helperText={formState.errors.amount?.message}
          disabled={isSubmitting}
          inputMode='numeric'
        />
        <PhoneNumberInput
          name='phone_number'
          control={control}
          error={!!formState.errors.phone_number}
          helperText={formState.errors.phone_number?.message}
          disabled={isSubmitting}
        />
        {providerConfig.otpOnInitialStep && otpInput()}
      </Box>
    );
  };

  const handleResendOtp = () => {
    const data = detailsSchema.parse(getValues());

    initiateDeposit({ ...data, phone_number: normalizePhoneNumber(data.phone_number) });
  };

  const renderOtpStep = () => (
    <Box sx={styles.stepContainer}>
      <Stack spacing={1} sx={styles.otpContainer}>
        <Typography
          variant='h6'
          sx={{
            color: 'neutral.25',
          }}
        >
          {t('otpVerification')}
        </Typography>
        <Typography
          variant='body2'
          sx={{
            color: 'neutral.300',
          }}
        >
          {t('otpSentToPhone')} +{getValues('phone_number')}
        </Typography>
      </Stack>
      {otpInput()}
      <Button variant='text' onClick={handleResendOtp} disabled={isInitiateDepositPending}>
        {t('resendOtp')}
      </Button>
    </Box>
  );

  return (
    <StyledForm
      key={currentStep}
      submitTitle={currentStep === FORM_STEPS.PAYMENT_DETAILS ? t('deposit') : t('verifyOtp')}
      onSubmit={handleFormSubmit}
      isSubmitting={isSubmitting}
      disablePadding
    >
      {currentStep === FORM_STEPS.PAYMENT_DETAILS ? renderPaymentStep() : renderOtpStep()}
    </StyledForm>
  );
};

export default LigdiCashDepositForm;
